import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { RefreshCw, Flame, Snowflake, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { NavBar } from "@/components/NavBar";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PERIODS = [7, 14, 30];

export default function Statistik() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(7);

  const load = useCallback(async (d, showToast = false) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/stats?days=${d}`);
      setData(res.data);
      if (showToast) toast.success("Statistik dikemaskini");
    } catch (e) {
      if (showToast) toast.error("Gagal muat statistik.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(days); }, [days, load]);

  const digits = data?.digits || [];
  const maxDigit = Math.max(1, ...digits.map((x) => x.count));
  const sortedByCount = [...digits].sort((a, b) => b.count - a.count);
  const hottestDigit = sortedByCount[0]?.digit;
  const coldestDigit = sortedByCount[sortedByCount.length - 1]?.digit;
  const hot = data?.hot || [];
  const maxHot = Math.max(1, ...hot.map((h) => h.count));

  return (
    <div className="min-h-screen bg-slate-50 pb-20">
      <NavBar />

      <div className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-[1500px] flex-wrap items-center gap-2.5 px-4 py-4 sm:px-6">
          <span className="flex items-center gap-1.5 text-sm font-semibold text-slate-600"><TrendingUp className="h-4 w-4" /> Tempoh:</span>
          {PERIODS.map((p) => (
            <button key={p} data-testid={`period-${p}`} onClick={() => setDays(p)}
              className={`rounded-full border px-4 py-1.5 text-sm font-semibold transition-colors ${
                days === p ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-100"
              }`}>
              {p} hari
            </button>
          ))}
          <button data-testid="refresh-stats" onClick={() => load(days, true)}
            className="flex flex-1 items-center justify-center gap-2 rounded-full bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-700 sm:ml-auto sm:flex-none sm:py-2">
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
      </div>

      <main className="mx-auto max-w-[1500px] px-4 pt-6 sm:px-6">
        {data && (
          <p className="mb-6 text-sm text-slate-500" data-testid="stats-summary">
            Berdasarkan <span className="font-semibold text-slate-800">{data.totalNumbers.toLocaleString()}</span> nombor dari semua pool dalam <span className="font-semibold text-slate-800">{data.days} hari</span> terakhir.
          </p>
        )}

        {/* Digit heatmap */}
        <section className="mb-10 rounded-2xl border border-slate-200 bg-white p-5 sm:p-6" data-testid="digit-heatmap">
          <h2 className="mb-1 font-display text-xl font-black text-slate-900">Digit Panas & Sejuk</h2>
          <p className="mb-5 text-sm text-slate-500">Kekerapan digit 0–9 muncul dalam semua nombor menang.</p>
          <div className="flex items-end gap-2 sm:gap-3">
            {digits.map((d) => {
              const h = Math.round((d.count / maxDigit) * 100);
              const isHot = d.digit === hottestDigit;
              const isCold = d.digit === coldestDigit;
              return (
                <div key={d.digit} className="flex flex-1 flex-col items-center gap-2" data-testid={`digit-${d.digit}`}>
                  <span className={`text-xs font-bold ${isHot ? "text-red-600" : isCold ? "text-blue-600" : "text-slate-400"}`}>{d.count}</span>
                  <div className="flex h-40 w-full items-end overflow-hidden rounded-lg bg-slate-100">
                    <motion.div
                      initial={{ height: 0 }}
                      animate={{ height: `${h}%` }}
                      transition={{ duration: 0.6, ease: "easeOut" }}
                      className={`w-full rounded-t-lg ${isHot ? "bg-red-500" : isCold ? "bg-blue-500" : "bg-slate-400"}`}
                    />
                  </div>
                  <span className={`font-display text-lg font-black ${isHot ? "text-red-600" : isCold ? "text-blue-600" : "text-slate-700"}`}>{d.digit}</span>
                </div>
              );
            })}
          </div>
          <div className="mt-4 flex gap-4 text-xs font-medium text-slate-500">
            <span className="flex items-center gap-1.5"><Flame className="h-4 w-4 text-red-500" /> Paling panas: <b className="text-red-600">{hottestDigit}</b></span>
            <span className="flex items-center gap-1.5"><Snowflake className="h-4 w-4 text-blue-500" /> Paling sejuk: <b className="text-blue-600">{coldestDigit}</b></span>
          </div>
        </section>

        {/* Hot 4D numbers */}
        <section data-testid="hot-numbers">
          <h2 className="mb-1 font-display text-xl font-black text-slate-900">Nombor 4D Paling Kerap</h2>
          <p className="mb-5 text-sm text-slate-500">Nombor yang paling banyak kali muncul (mana-mana hadiah, semua pool).</p>
          {loading ? (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-6">
              {Array.from({ length: 12 }).map((_, i) => <div key={i} className="h-20 animate-pulse rounded-xl bg-slate-100" />)}
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-6">
              {hot.map((h, i) => (
                <motion.div key={h.number} initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: i * 0.02 }}
                  data-testid={`hot-${h.number}`}
                  className="flex flex-col items-center rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
                  <span className="font-display tabular text-2xl font-black tracking-tight text-slate-900">{h.number}</span>
                  <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                    <div className="h-full rounded-full bg-red-500" style={{ width: `${(h.count / maxHot) * 100}%` }} />
                  </div>
                  <span className="mt-1.5 text-xs font-semibold text-slate-500">{h.count}x</span>
                </motion.div>
              ))}
            </div>
          )}
        </section>

        <footer className="mt-10 border-t border-slate-200 pt-6 text-center text-xs text-slate-400">
          <p>Statistik dikira dari result live yang dikumpul. Hanya rujukan — bukan ramalan.</p>
        </footer>
      </main>
    </div>
  );
}
